import { observable, computed, action } from "mobx";
import locale from "@bonliva-crm/locale";
import moment from "moment";
import {
  SharedFiles,
  SharedFileSearch_Upload as upload,
  UpdateSharedFile,
  GetSharedFile,
  RemoveSharedFile
} from "@graphql";
import { store } from "@store";
import { VirtualSearchManager } from "@search";
import { OkCancelDialogOptions } from "@containers/ConfirmDialog";
import { initialFilter, Row } from "./utils";
import { SharedFileFilterManager } from "./SharedFileFilterManager";
import { UploadedFile } from "../SharedFilesUploadedDialog";
import { EditableFile } from "../SharedFileEdit/SharedFilesEditDialog";
import { SharedFileEditStore } from "../SharedFileEdit/SharedFileEditStore";
import { SharedFile } from "../SharedFileUploader";
import { createObjectID } from "@bonliva-crm/common";

export class SharedFileSearchStore {
  tableManager: VirtualSearchManager<Row>;
  filterManager: SharedFileFilterManager;
  editStore = new SharedFileEditStore();

  @observable isLoading = false;
  @observable filesToUpload: SharedFile[] = [];

  @observable private _uploadedFiles: UploadedFile[] = [];
  @observable private _editableFile: EditableFile[] = [];

  constructor() {
    this.fetch = this.fetch.bind(this);
    this.filterManager = new SharedFileFilterManager({ initialFilter }, this.fetch);
    this.tableManager = new VirtualSearchManager(this.fetch);

    (window as any).sharedFileSearchStore = this;
  }

  @computed get isUploadedFilesDialogOpen() {
    return this._uploadedFiles.length > 0;
  }

  @computed get isEditFilesDialogOpen() {
    return this._editableFile.length > 0;
  }

  @computed get uploadedFiles() {
    return this._uploadedFiles;
  }

  fetch(skip?: number, limit?: number) {
    const _skip = skip == null ? 0 : skip;
    const _limit = limit == null ? 20 : limit;
    this.isLoading = true;

    return SharedFiles.func(
      {
        filter: this.filterManager.filter,
        skip: _skip,
        limit: _limit
      },
      { fetchPolicy: "network-only" }
    )
      .then(({ data }) => {
        if (data.sharedFiles.skip === 0) {
          this.tableManager.clear();
        }

        const avaliableFiles = data.sharedFiles.files.filter(
          (file) => file.Deleted !== true
        );

        this.tableManager.push(avaliableFiles);
        this.tableManager.refresh();
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  edit = (id: string, index: number) => {
    GetSharedFile.func({
      fileId: id
    })
      .then((d) => {
        const file = d.data.sharedFileById;
        const parsedFile: any = {
          name: file.FileRef.Name,
          size: file.FileRef.Size,
          id: file.FileRef.NodeID,
          expirationDate: file.ExpiresOn
        };

        this.editStore.open({
          closeOnSuccess: true,
          files: [parsedFile],
          onSubmit: (_files, expirationDate) => {
            return UpdateSharedFile.func({
              expiresOn: expirationDate,
              id: _files[0].id,
              fileName: _files[0].name
            })
              .then((result) => {
                this.tableManager.updateRow(index, result.data.updateSharedFile);
                this.tableManager.refresh();
                store().snackbars.success(locale.fileHasBeenModified);
              })
              .catch((e) => {
                store().snackbars.error(`Cannot edit file\nReason: ${e}`);
              });
          }
        });
      })
      .catch((err) => {
        console.error(err);
      });
  };

  deleteFile = (id: string, index: number) => {
    store()
      .ui.confirmDialog.confirm(new OkCancelDialogOptions())
      .then((result) => {
        if (result !== "ok") {
          return;
        }

        RemoveSharedFile.func({
          fileId: id
        })
          .then((d) => {
            this.tableManager.deleteRow(index);
            this.tableManager.refresh();

            store().snackbars.success(locale.fileHasBeenRemoved);
          })
          .catch((e) => {
            store().snackbars.error(`Cannot remove file\nReason: ${e}`);
          });
      });
  };

  @action.bound handleUploadFile() {
    store()
      .upload.requestFileUpload({ multiple: true })
      .then((files) => {
        this.addUploadFiles(files);
      });
  }

  @action.bound addUploadFiles(files: File[]) {
    this.filesToUpload = files.map((x) => ({
      id: createObjectID(),
      file: x,
      name: x.name,
      expiration: "week" as const
    }));
  }

  @action.bound async uploadFiles() {
    if (this.filesToUpload.length === 0) {
      return;
    }

    const promises = this.filesToUpload.map((file, i) => {
      const expiresOn = moment().add(1, file.expiration).toDate();

      return upload.func({
        expiresOn,
        file: file.file,
        fileName: file.name
      });
    });

    return Promise.all(promises).then((results) => {
      this.filesToUpload = [];

      const uploaded = results.map((r) => {
        this.tableManager.prependRow(r.data.uploadSharedFile);
        return {
          name: r.data.uploadSharedFile.Name,
          url: r.data.uploadSharedFile.ShareURL
        };
      });

      this.tableManager.refresh();
      this.openSharedFilesUploadedDialog(uploaded);
      store().snackbars.success(locale.uploadHasFinished);
    });
  }

  @action.bound openSharedFilesUploadedDialog(files: UploadedFile[]) {
    this._uploadedFiles = files;
  }

  @action.bound closeSharedFilesUploadedDialog() {
    this._uploadedFiles = [];
  }

  @action.bound closeUploaderDialog() {
    this.filesToUpload = [];
  }

  @action.bound updateUploadFile(fileId: string, data: Partial<SharedFile>) {
    const fileIndex = this.filesToUpload.findIndex((x) => x.id === fileId);

    if (fileIndex < 0) {
      return;
    }

    this.filesToUpload[fileIndex] = {
      ...this.filesToUpload[fileIndex],
      ...data
    };
  }
}
